import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiBookOpen,
  FiCreditCard,
  FiGrid,
  FiLogIn,
  FiUser,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';
import { notificationService } from '../services/api';
import BrandLogo from './BrandLogo';
import LearningContextSwitcher from './LearningContextSwitcher';
import './Navbar.css';

const LEARN_PATHS = [
  '/exercise', '/quiz', '/flashcards', '/reading', '/memory', '/writing', '/review',
  '/conversation', '/class', '/learn', '/progress', '/level-check', '/level-tests',
  '/context', '/learning-personalization', '/cohorts',
];

function Navbar({ isGuest, onGuestExit, userRole }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId');
  const hasToken = !!localStorage.getItem('token');
  const [unreadCount, setUnreadCount] = useState(0);
  // Learning navigation belongs to every signed-in account. Staff keep the
  // same Learn / Community / Profile foundation and receive Admin as an extra
  // privileged destination. This also keeps the foundation stable while the
  // authoritative role refresh completes after startup.
  const isAuthenticatedUser = !isGuest && hasToken;
  const isStaff = isAuthenticatedUser && ['admin', 'marketing'].includes(userRole);

  useEffect(() => {
    if (isGuest || !userId || !hasToken) return undefined;
    let cancelled = false;
    const load = () => notificationService.unreadCount()
      .then((res) => { if (!cancelled) setUnreadCount(res.data?.unreadCount || 0); })
      .catch(() => {});
    load();
    const id = window.setInterval(load, 60000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [isGuest, userId, hasToken]);

  useEffect(() => {
    const onUpdate = (event) => setUnreadCount(Math.max(0, Number(event.detail?.unreadCount) || 0));
    window.addEventListener('notificationsUpdated', onUpdate);
    return () => window.removeEventListener('notificationsUpdated', onUpdate);
  }, []);

  const handleSignUp = () => {
    onGuestExit?.();
    navigate('/select-language?mode=register');
  };

  const handleLogin = () => {
    onGuestExit?.();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  const isActiveSection = (paths) => paths.some((path) => (
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  ));
  const practiceActive = isActiveSection(LEARN_PATHS);
  const learnActive = location.pathname === '/' || practiceActive;
  const communityActive = isActiveSection(['/community']);
  const profileActive = isActiveSection(['/profile', '/billing', '/institution']);

  const profileIcon = (
    <span className="nav-icon-wrap">
      <FiUser aria-hidden="true" />
      {!profileActive && unreadCount > 0 && (
        <span
          className="nav-notif-badge"
          aria-label={t('notifications.unreadCount', {
            count: unreadCount,
            defaultValue: '{{count}} unread',
          })}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </span>
  );

  return (
    <nav
      className={`navbar${isAuthenticatedUser ? ' authenticated-navigation' : ''}`}
      aria-label={t('navbar.primaryNavigation', 'Primary navigation')}
    >
      <div className="navbar-container">
        <div className="navbar-identity">
          {/* The brand remains the visual anchor even when the optional program
              switcher is hidden for a single-commitment account. */}
          <Link to="/" className="navbar-brand" aria-label={t('common.backToHome', 'Back to home')}>
            <BrandLogo variant="mark" className="brand-logo" decorative={false} />
            <span className="navbar-brand-name">Lingo Booth</span>
          </Link>

          {!isGuest && hasToken && <LearningContextSwitcher />}

          {isGuest && (
            <div className="guest-banner">
              <span className="guest-icon" aria-hidden="true">{'👋'}</span>
              <span className="guest-text">{t('navbar.guestMode', 'Guest Mode')}</span>
            </div>
          )}
        </div>

        <ul className="nav-menu">
          {isAuthenticatedUser && (
            <>
              <li className="nav-item">
                <Link to="/" className={`nav-link ${learnActive ? 'active' : ''}`}>
                  <span className="nav-icon"><FiBookOpen aria-hidden="true" /></span>
                  <span className="nav-text">{t('navbar.learn', 'Learn')}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/community" className={`nav-link ${communityActive ? 'active' : ''}`}>
                  <span className="nav-icon"><FiUsers aria-hidden="true" /></span>
                  <span className="nav-text">{t('navbar.community', 'Community')}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/profile" className={`nav-link ${profileActive ? 'active' : ''}`}>
                  <span className="nav-icon">{profileIcon}</span>
                  <span className="nav-text">{t('navbar.profile', 'Profile')}</span>
                </Link>
              </li>
            </>
          )}

          {isStaff && (
            <li className="nav-item nav-item-admin">
              <Link to="/admin" className={`nav-link nav-admin ${isActiveSection(['/admin']) ? 'active' : ''}`}>
                <span className="nav-icon"><FiGrid aria-hidden="true" /></span>
                <span className="nav-text">{t('navbar.admin', 'Admin')}</span>
              </Link>
            </li>
          )}

          {isGuest && (
            <>
              <li className="nav-item">
                <Link to="/exercise" className={`nav-link ${practiceActive ? 'active' : ''}`}>
                  <span className="nav-icon"><FiBookOpen aria-hidden="true" /></span>
                  <span className="nav-text">{t('navbar.exercise', 'Exercise')}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/pricing" className={`nav-link ${isActive('/pricing') || isActive('/billing') ? 'active' : ''}`}>
                  <span className="nav-icon"><FiCreditCard aria-hidden="true" /></span>
                  <span className="nav-text">{t('navbar.plans', 'Plans')}</span>
                </Link>
              </li>
              <li className="nav-item">
                <button type="button" className="nav-link btn-auth" onClick={handleLogin}>
                  <span className="nav-icon"><FiLogIn aria-hidden="true" /></span>
                  <span className="nav-text">{t('navbar.login', 'Login')}</span>
                </button>
              </li>
              <li className="nav-item">
                <button type="button" className="nav-link btn-signup" onClick={handleSignUp}>
                  <span className="nav-icon"><FiUserPlus aria-hidden="true" /></span>
                  <span className="nav-text">{t('navbar.signUpFree', 'Sign Up Free')}</span>
                </button>
              </li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
